import { useContext, useEffect, useState } from 'react';

import { qCode } from '@/modules/articles/model/concept-item';
import { Article } from '@/modules/articles/model/article';
import { useSubscription, MessageServiceConstants } from '@/core/messaging/export';
import {
  useWidgetLoaderData,
  WidgetContainer,
  WidgetContainerContext,
} from '@/core/widgets/export';
import { useWidgetNotifications } from '@/core/notifications/export';
import { Loading } from '@/shared/components/export';
import type { PinnedWidgetState } from '@/modules/articles/widgets/pinned/pinned-series-widget';
import PinnedArticle from '@/modules/articles/components/pins/pinned-article';
import { UpgradePanel, ComingSoonPanel } from '@/shared/session/export';
import { useSessionIdentity } from '@/core/auth/export';

export default function PinnedArticlePage({
  className,
  widgetLink,
}: {
  className?: string;
  widgetLink?: React.ReactNode;
}) {
  const { article, title, genre, loaded, comingSoon } = useWidgetLoaderData() as {
    article: Article;
    comingSoon?: boolean;
    title: string;
    genre: string;
    loaded: boolean;
  };
  const identity = useSessionIdentity();

  const widgetContainer = useContext<WidgetContainer>(WidgetContainerContext);

  const { sendNotification } = useWidgetNotifications();

  const [newVersionAvailable, setNewVersionAvailable] = useState<boolean>(false);

  useSubscription<Article>(MessageServiceConstants.NEWS_ARTICLE_TOPIC, message => {
    if (genre) {
      if (
        // eslint-disable-next-line
        message.genre.some(value => qCode(value) == genre) ||
        // eslint-disable-next-line
        message.subject.some(value => qCode(value) == genre)
      ) {
        setNewVersionAvailable(true);

        sendNotification({
          correlationId: `pinned-${article.uri}`,
          title: 'Pinned Series Update: ' + title,
          description: article.headline,
          durationMillis: 300_000,
          date: new Date(),
        });
      }
    }
  });

  useEffect(() => {
    widgetContainer.updateState<PinnedWidgetState>({ title });
    // eslint-disable-next-line
  }, [title]);

  if (!loaded)
    return (
      <>
        <div className={''}>
          <Loading type={'grid'} />
        </div>
      </>
    );

  if (!article) {
    if (comingSoon) {
      return (
        <ComingSoonPanel
          contentName={'Pinned Series: ' + genre}
          title={title}
          identity={identity}
        />
      );
    }

    return <UpgradePanel contentName={'Pinned Series: ' + genre} identity={identity} />;
  }

  return (
    <PinnedArticle
      article={article as Article}
      className={className}
      newVersionAvailable={newVersionAvailable}
      widgetLink={widgetLink}
    />
  );
}
